import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import {
  getAssessment,
  getAssessmentResultList,
  getAssessmentResultsOverview,
  updateAssessment,
} from '../../../../../utils/api_service/assessment_api';
import { deleteAssessmentUser } from '../../../../../utils/api_service/assessments_api';
import {
  getCandidateApplicants,
  getJobOpeningDetails,
  sendInviteApplicants,
} from '../../../../../utils/api_service/JobOpenings';
import { CandidateStatusEnum } from '../../../../../utils/helpers/candidateStatus';
import { useDebounce } from '../../../../../utils/hooks/useDebounce';
import {
  AssessmentType,
  AssessmentUser,
  BasicInfoInputs,
  CandidateStatus,
  IAssessmentResultOverview,
  ResultRequestResponse,
  UserAssessmentResponse,
} from '../../../../../utils/types/assessment_types';
import {
  AutoAssessment,
  CandidateJobApplicantsData,
  JobOpeningDetail,
  sortCandidates,
} from '../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../utils/types/react_select_types';
import { CandidateListType } from '../../JobOpeningDetails/AutoAssessmentTab/TabContent/CandidateInvites';

export const useAssessmentQuestions = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const [assessmentUserListResponse, setAssessmentUserListResponse] =
    useState<ResultRequestResponse>();
  const [overviewResultResponse, setOverviewResultResponse] =
    useState<IAssessmentResultOverview>();

  // Initialize from URL search params
  const [sidebarTab, setSidebarTab] = useState<'Config' | 'Results'>(
    (searchParams.get('sidebarTab') as 'Config' | 'Results') || 'Config',
  );
  const [activeTab, setActiveTab] = useState<number>(
    parseInt(searchParams.get('assessmentTab') || '0'),
  );
  const [highestResultScore, setHighestResultScore] = useState<number>();
  const [assessmentState, setAssessmentState] = useState<{
    isLoading: boolean;
    error: string | null;
    hasData: boolean;
  }>({
    isLoading: true,
    error: null,
    hasData: false,
  });

  // Dynamic tab titles based on available data
  // Group sub-tabs
  const configTabs = ['Settings', 'Invitation Template', 'Candidates'];

  const getResultsTabs = () => {
    const resultsTabs = ['Results'];
    if (assessmentUserListResponse?.assessmentUserList) {
      const userList = assessmentUserListResponse.assessmentUserList;
      if (
        userList.some(
          (user) => user.candidateStatus === CandidateStatusEnum.WAITLISTED,
        )
      ) {
        resultsTabs.push(CandidateStatusEnum.WAITLISTED);
      }
      if (
        userList.some(
          (user) => user.candidateStatus === CandidateStatusEnum.SHORTLISTED,
        )
      ) {
        resultsTabs.push(CandidateStatusEnum.SHORTLISTED);
      }
      if (
        userList.some(
          (user) => user.candidateStatus === CandidateStatusEnum.UNSUCCESSFUL,
        )
      ) {
        resultsTabs.push(CandidateStatusEnum.UNSUCCESSFUL);
      }
    }
    return resultsTabs;
  };

  const resultsTabs = getResultsTabs();
  const tabTitles = sidebarTab === 'Config' ? configTabs : resultsTabs;

  const [autoAssessment, setAutoAssessment] = useState<AutoAssessment>();

  // Adjust active tab if current tab is no longer available
  useEffect(() => {
    if (tabTitles.length > 0 && activeTab >= tabTitles.length) {
      setActiveTab(0);
    }
  }, [tabTitles.length, activeTab]);

  useEffect(() => {
    const fetchAssessmentData = async () => {
      if (!autoAssessment?.assessment?.id) {
        setAssessmentState({
          isLoading: false,
          error: 'No assessment configured for this job opening',
          hasData: false,
        });
        return;
      }

      setAssessmentState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const [resultListResponse, overviewResponse] = await Promise.all([
          getAssessmentResultList(autoAssessment.assessment.id),
          getAssessmentResultsOverview(autoAssessment.assessment.id),
        ]);

        const hasResults = resultListResponse?.assessmentUserList?.length > 0;
        const hasSkills =
          overviewResponse?.skills?.length > 0 ||
          overviewResponse?.behaviorSkills?.length > 0;

        if (!hasResults) {
          setAssessmentState({
            isLoading: false,
            error: null,
            hasData: false,
          });
          return;
        }

        setAssessmentUserListResponse(resultListResponse);
        if (hasSkills) {
          setOverviewResultResponse(overviewResponse);
        }

        // Calculate highest score
        const validScores = resultListResponse.assessmentUserList
          .filter(
            (result: UserAssessmentResponse) =>
              result.maxScore !== undefined && result.maxScore > 0,
          )
          .map((result: UserAssessmentResponse) => {
            const score = (result.score / (result.maxScore as number)) * 100;
            return isFinite(score) ? Math.min(score, 100) : 0;
          });

        if (validScores.length > 0) {
          setHighestResultScore(Math.round(Math.max(...validScores)));
        } else {
          setHighestResultScore(0);
        }

        setAssessmentState({
          isLoading: false,
          error: null,
          hasData: true,
        });
      } catch (error) {
        setAssessmentState({
          isLoading: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to load assessment data',
          hasData: false,
        });
      }
    };

    fetchAssessmentData();
  }, [autoAssessment?.assessment?.id]);
  //
  useEffect(() => {
    getJobOpeningDetails(id)
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          throw new Error('Error Fetching Job Requisition');
        }
      })
      .then(async (originalData: JobOpeningDetail) => {
        if (originalData.autoAssessment) {
          // Fetch full assessment details to ensure we have startDate/endDate
          if (originalData.autoAssessment.assessment?.id) {
            try {
              const fullAssessment = await getAssessment(
                originalData.autoAssessment.assessment.id,
              );
              if (fullAssessment) {
                originalData.autoAssessment.assessment = {
                  ...originalData.autoAssessment.assessment,
                  ...fullAssessment,
                };
              }
            } catch (err) {
              console.error('Failed to fetch full assessment details', err);
            }
          }
          setAutoAssessment(originalData.autoAssessment);
        } else {
          console.log('⚠️ No autoAssessment found');
        }
      });
  }, [id, activeTab]);

  const [assessmentFormInfo, setAssessmentFormInfo] =
    useState<{
      startDate: string;
      endDate: string;
      title: string;
      description: string;
      type: string;
      jobPosition: string;
    } | null>(null);

  useEffect(() => {
    if (autoAssessment?.assessment) {
      const toISODateStr = (timestamp: any): string | null => {
        if (!timestamp) return null;
        const num = Number(timestamp);
        if (isNaN(num) || num === 0) return null;
        // Handle seconds (10 digits) vs milliseconds (13+ digits)
        const ms = num < 10000000000 ? num * 1000 : num;
        const d = new Date(ms);
        if (isNaN(d.getTime()) || d.getFullYear() <= 1970) return null;
        return d.toISOString().split('T')[0];
      };

      const start =
        autoAssessment.assessment.startDate ??
        (autoAssessment.assessment as any).start_date;
      const end =
        autoAssessment.assessment.endDate ??
        (autoAssessment.assessment as any).end_date;

      const startStr =
        toISODateStr(start) ?? new Date().toISOString().split('T')[0];
      // Default endDate to 1 week after startDate if missing/invalid
      const oneWeekLater = new Date(
        new Date(startStr).getTime() + 7 * 24 * 60 * 60 * 1000,
      )
        .toISOString()
        .split('T')[0];
      const endStr = toISODateStr(end) ?? oneWeekLater;

      setAssessmentFormInfo({
        startDate: startStr,
        endDate: endStr,
        title: autoAssessment.assessment.assessmentTitle ?? '',
        description: autoAssessment.assessment.description ?? '',
        type: autoAssessment.assessment.assessmentType ?? 'candidate',
        jobPosition: autoAssessment.assessment.jobPosition ?? '',
      });
    }
  }, [autoAssessment?.assessment]);

  const changeDate = (timeRange: {
    startDate: string | null;
    endDate: string | null;
  }) => {
    if (assessmentFormInfo) {
      setAssessmentFormInfo({
        ...assessmentFormInfo,
        startDate: timeRange.startDate ?? assessmentFormInfo.startDate,
        endDate: timeRange.endDate ?? assessmentFormInfo.endDate,
      });
    }
  };

  const handleDataChange = async () => {
    if (autoAssessment?.assessment?.id && assessmentFormInfo) {
      // The backend expects ISO date strings (YYYY-MM-DD), not Unix timestamps.
      // Sending timestamps causes a mongoengine ValidationError.
      const payload: BasicInfoInputs = {
        title: assessmentFormInfo.title,
        description: assessmentFormInfo.description,
        type: assessmentFormInfo.type as AssessmentType,
        startDate: assessmentFormInfo.startDate,
        endDate: assessmentFormInfo.endDate,
        jobPosition: assessmentFormInfo.jobPosition,
      };

      try {
        const raw = await updateAssessment(
          payload,
          autoAssessment.assessment.id,
        );
        // API returns either [{...}] (array) or {...} (object)
        const updated = Array.isArray(raw) ? raw[0] : raw;

        if (updated?.id) {
          // Merge the updated fields back into state
          setAutoAssessment((prev) =>
            prev
              ? {
                ...prev,
                assessment: {
                  ...prev.assessment,
                  startDate: updated.startDate,
                  endDate: updated.endDate,
                  assessmentTitle:
                      updated.assessmentTitle ??
                      updated.title ??
                      prev.assessment?.assessmentTitle,
                  description:
                      updated.description ?? prev.assessment?.description,
                  jobPosition:
                      updated.jobPosition ?? prev.assessment?.jobPosition,
                },
              }
              : prev,
          );
          toast.success('Assessment dates updated successfully!', {
            position: 'bottom-center',
          });
        } else {
          toast.error('Failed to update assessment dates. Please try again.', {
            position: 'bottom-center',
          });
        }
      } catch (error) {
        toast.error('Failed to update assessment dates.', {
          position: 'bottom-center',
        });
      }
    }
  };

  const updateAssessmentUserList = (
    selectedRows: AssessmentUser[],
    candidateStatus: CandidateStatus,
  ) => {
    if (assessmentUserListResponse) {
      const updatedAssessmentUserListResponse = {
        ...assessmentUserListResponse,
      };
      selectedRows.forEach((selectedRow) => {
        const userIndex =
          updatedAssessmentUserListResponse.assessmentUserList.findIndex(
            (user) => user.id === selectedRow.assessmentUserId,
          );
        if (userIndex !== -1) {
          updatedAssessmentUserListResponse.assessmentUserList[
            userIndex
          ].candidateStatus = candidateStatus;
        }
      });
      setAssessmentUserListResponse(updatedAssessmentUserListResponse);
    }
  };
  // Invite Candidates
  const [candidateInviteObject, setCandidateInviteObject] =
    useState<CandidateJobApplicantsData>({
      applicantStatus: [],
      assessmentStatus: [],
      email: '',
      name: '',
      invited: false,
    }); // Local state for immediate input feedback
  const [localName, setLocalName] = useState('');
  const [localEmail, setLocalEmail] = useState('');
  // Debounced search values
  const debouncedName = useDebounce(localName, 800);
  const debouncedEmail = useDebounce(localEmail, 800);
  const [candidatesList, setCandidatesList] = useState<CandidateListType[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<Set<string>>(
    new Set(),
  );
  const [limit, setLimit] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(true);
  const [sort, setSort] = useState<sortCandidates>({ firstName: 'asc' });
  const fetchCandidateApplicants = async (pageArg?: number) => {
    setLoading(true);
    const pageToFetch = pageArg ?? pageNumber;

    try {
      const response = await getCandidateApplicants(
        id,
        {
          ...candidateInviteObject,
          name: debouncedName,
          email: debouncedEmail,
        },
        pageToFetch,
        limit,
      );
      const data = await response.json();

      let list = data.applicants as CandidateListType[];
      const sortEntries = Object.entries(sort);
      if (sortEntries.length) {
        list = [...list].sort((a, b) => {
          for (const [key, direction] of sortEntries) {
            const aVal = (a as any)[key];
            const bVal = (b as any)[key];
            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }

      setCandidatesList(list);
      setTotalRecords(data.total_count);
      setSelectedCandidates([]);
      setSelectedCandidateIds(new Set());
      setTotalPages(Math.ceil(data.total_count / limit));
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Error Getting Job Applicants: ${error.message}`, {
          position: 'bottom-center',
        });
      } else {
        throw error;
      }
      setCandidatesList([]);
      setSelectedCandidates([]);
      setSelectedCandidateIds(new Set());
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchCandidateApplicants();
  }, [pageNumber, candidateInviteObject, limit]);

  const sendInvites = async () => {
    if (selectedCandidates.length === 0) {
      toast.warning('Please select candidates to send invites', {
        position: 'bottom-center',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await sendInviteApplicants(id, selectedCandidates);
      await response.json();
      await fetchCandidateApplicants();
      toast.success(
        `Invitation Sent To ${selectedCandidates.length} Applicant(s)`,
        {
          position: 'bottom-center',
        },
      );
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Error Sending Invite Applicants: ${error.message}`, {
          position: 'bottom-center',
        });
      } else {
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCandidate = async (
    targetId?: string,
    targetName?: string,
  ) => {
    if (!autoAssessment?.assessment?.id) return;

    let userAssessmentIdsToProcess: string[] = [];

    if (targetId) {
      const candidate = candidatesList.find((c) => c.id === targetId);
      if (candidate?.userAssessmentId) {
        userAssessmentIdsToProcess = [candidate.userAssessmentId];
      }
    } else {
      userAssessmentIdsToProcess = candidatesList
        .filter((c) => selectedCandidateIds.has(c.id) && c.userAssessmentId)
        .map((c) => c.userAssessmentId as string);
    }

    if (userAssessmentIdsToProcess.length === 0) {
      toast.warning('Please select candidates with invites to remove', {
        position: 'bottom-center',
      });
      return;
    }

    setLoading(true);
    try {
      await deleteAssessmentUser(
        autoAssessment.assessment.id,
        userAssessmentIdsToProcess,
      );
      toast.success(`Removed ${userAssessmentIdsToProcess.length} invite(s)`, {
        position: 'bottom-center',
      });

      if (!targetId || selectedCandidateIds.has(targetId)) {
        const newSelectedIds = new Set(selectedCandidateIds);
        if (targetId) {
          newSelectedIds.delete(targetId);
        } else {
          newSelectedIds.clear();
        }
        setSelectedCandidateIds(newSelectedIds);
      }
      await fetchCandidateApplicants();
    } catch (error) {
      toast.error('Failed to remove the invite', {
        position: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSort((prev) => {
      const next = { ...prev };
      if (next[key as keyof sortCandidates] === direction) {
        delete next[key as keyof sortCandidates];
      } else {
        next[key as keyof sortCandidates] = direction;
      }
      return next;
    });
    setPageNumber(1);
  };
  const isRowSelected = (rowIndex: number, row: CandidateListType) => {
    return selectedCandidateIds.has(row.id);
  };
  useEffect(() => {
    setCandidateInviteObject((prev) => ({
      ...prev,
      name: debouncedName,
      email: debouncedEmail,
    }));
    setPageNumber(1);
  }, [debouncedName, debouncedEmail]);
  const handleSelectRow = (rowIndex: number, row: CandidateListType) => {
    const newSelection = new Set(selectedCandidateIds);
    if (newSelection.has(row.id)) {
      newSelection.delete(row.id);
    } else {
      newSelection.add(row.id);
    }
    setSelectedCandidateIds(newSelection);
  };
  const handleSelectAll = () => {
    if (selectedCandidateIds.size === candidatesList.length) {
      setSelectedCandidateIds(new Set());
    } else {
      const allIds = new Set(candidatesList.map((candidate) => candidate.id));
      setSelectedCandidateIds(allIds);
    }
  };
  const handleStatusListChange = (
    label: string,
    selectedValue: SelectOptions[],
  ) => {
    const values = selectedValue.map((item) => item.value);
    setCandidateInviteObject((prev) => ({
      ...prev,
      [label]: values,
    }));
    setPageNumber(1);
  };
  const onPageChange = (page: number) => {
    setPageNumber(page);
  };
  useEffect(() => {
    setSelectedCandidates(Array.from(selectedCandidateIds));
  }, [selectedCandidateIds]);

  const isAllSelected =
    selectedCandidateIds.size === candidatesList.length &&
    candidatesList.length > 0;
  const isIndeterminate =
    selectedCandidateIds.size > 0 &&
    selectedCandidateIds.size < candidatesList.length;
  return {
    activeTab,
    setActiveTab,
    sidebarTab,
    setSidebarTab,
    tabTitles,
    configTabs,
    resultsTabs,
    autoAssessment,
    assessmentUserListResponse,
    overviewResultResponse,
    highestResultScore,
    updateAssessmentUserList,
    assessmentState,
    assessmentFormInfo,
    changeDate,
    handleDataChange,
    // Invite Candidates
    candidatesList,
    selectedCandidates,
    selectedCandidateIds,
    limit,
    loading,
    sort,
    fetchCandidateApplicants,
    sendInvites,
    handleRemoveCandidate,
    handleSortChange,
    isRowSelected,
    handleSelectRow,
    handleSelectAll,
    handleStatusListChange,
    onPageChange,
    isAllSelected,
    isIndeterminate,
    pageNumber,
    totalPages,
    totalRecords,
    candidateInviteObject,
    setLocalName,
    setLocalEmail,
    localName,
    localEmail,
  };
};
